import { isGaffRetained } from "@/lib/affdo-fit";
import type { DihedralProfile } from "@/api/affdo";
import type { ReferenceLevel } from "@/components/submission/affdo-flow-adapter";

/**
 * How a `DihedralProfile` is read before it is plotted — shared by the
 * per-run panel (`DihedralProfilePlot`) and the cross-run overlay
 * (`TorsionOverlayChart`).
 *
 * These four helpers moved out of `DihedralProfilePlot` when VIZ U2.1 added a
 * second CHART SHAPE over the same artifacts. The overlay is not a fork of the
 * panel — a panel draws one torsion's three curves, an overlay draws one curve
 * per (run, torsion) on shared axes, and no amount of props makes those the
 * same component. What must not diverge is the READING: which arrays are
 * trusted, and above all *when the absence of a fitted curve may be captioned
 * as a scientific decision*. That rule (#869/#878, and Rupert's block on #878)
 * now has exactly one implementation, so a change to it reaches both surfaces
 * or neither.
 *
 * The two vague-but-true fallbacks are exported as constants (#1370) because a
 * THIRD reader needs them: the shared key over a grid of small multiples falls
 * back to them when the panels beneath it do not all declare the same level or
 * the same base force field. A second spelling of `"MM initial"` there would be
 * a label that silently stops matching this one.
 */

/**
 * Tolerant of a payload that does not carry the expected arrays.
 *
 * These field names changed shape once already; a client that dereferences
 * `.length` on whatever arrived throws a TypeError and takes down the whole
 * grid, when the caller has a perfectly good "no profile available"
 * placeholder one branch away. A skewed or stale payload should degrade to
 * that placeholder, not to a blank page.
 */
export function numbers(value: unknown): number[] {
  return Array.isArray(value) ? (value as number[]) : [];
}

/**
 * The series names must not be hard-coded. `mm_profile_initial` is GAFF2 on
 * today's AFFDO path, but that is a value the artifact *declares*
 * (`TorsionScanMMProfile.force_field`), not something a chart may assert: a
 * reader will quote a legend as evidence about GAFF2 specifically, and a base
 * force field that ever varies would make the label a silent lie. Falls back
 * to the vague-but-true "MM initial" when the artifact is silent.
 */
export const MM_SERIES_FALLBACK = "MM initial";

export function mmSeriesName(profile: DihedralProfile): string {
  const declared = profile.mm_initial_force_field;
  return typeof declared === "string" && declared.length > 0 ? declared : MM_SERIES_FALLBACK;
}

/**
 * The reference curve's name — the same rule as {@link mmSeriesName}, one
 * curve over: read off the artifact, never asserted (#1365, #2026).
 *
 * **The name is the record's REFERENCE LEVEL, written `energy//geometry`.** A
 * level that optimises at its own method is named by it (`Reference (DFT)`,
 * `Reference (MACE)`); a single-point level evaluates its energies at the XTB
 * scan's geometries and is named for both (`Reference (DFT//XTB)`). This
 * extends AFFDO main's own `dft-sp` label (`DFT//XTB`, AFFDO `affdo.py:8650`,
 * `src/plotmod.py:558-560`) to every single-point level — and **diverges from
 * AFFDO deliberately** for the two ML ones: AFFDO prints `MACE-SP`/`QDPI2-SP`,
 * this prints `MACE//XTB`/`QDPI2//XTB`, so that one rule names every
 * single-point level the same way (operator decision, flowstack#2026).
 *
 * The level comes from `DihedralProfile.reference_level`, which the server
 * projects only from what the record declares. Before #2026 the name was built
 * from `qm_method`, which only DFT records carry, so every ML and xtb run's
 * curve read the literal `"QM reference"` — false for all of them. The DFT
 * method/basis is now the tooltip's detail ({@link qmSeriesDetail}).
 *
 * A level the record does not declare, or one this table does not know, reads
 * the bare `"Reference"` — true of any level, and the only honest thing to say
 * about one nobody recorded. Asserting a default here would mislabel every run
 * whose level differs from it: a claim about the science, made by a stylesheet.
 */
export const QM_SERIES_FALLBACK = "Reference";

const REFERENCE_LEVEL_NAMES: Readonly<Record<ReferenceLevel, string>> = {
  xtb: "XTB",
  dft: "DFT",
  "dft-sp": "DFT//XTB",
  mace: "MACE",
  "mace-sp": "MACE//XTB",
  qdpi2: "QDPI2",
  "qdpi2-sp": "QDPI2//XTB",
};

function referenceLevelName(level: unknown): string | undefined {
  return typeof level === "string" && Object.hasOwn(REFERENCE_LEVEL_NAMES, level)
    ? REFERENCE_LEVEL_NAMES[level as ReferenceLevel]
    : undefined;
}

export function qmSeriesName(profile: DihedralProfile): string {
  const name = referenceLevelName(profile.reference_level);
  return name === undefined ? QM_SERIES_FALLBACK : `${QM_SERIES_FALLBACK} (${name})`;
}

/**
 * A declared level read as its two halves: the method the ENERGY was computed
 * at, and — for a single-point level only — the method the GEOMETRY came from.
 *
 * Read off {@link REFERENCE_LEVEL_NAMES}'s `energy//geometry` spelling rather
 * than from a second table of level strings, so the curve's name and the
 * structure panel's label cannot disagree about a level (#2028). `undefined`
 * for a level the record does not declare or this table does not know — the
 * same reading {@link qmSeriesName} gives the bare `"Reference"`.
 */
export interface ReferenceLevelParts {
  readonly energy: string;
  /** Set only on a single-point level: the geometry its energy was evaluated on. */
  readonly geometry?: string;
}

export function referenceLevelParts(level: unknown): ReferenceLevelParts | undefined {
  const name = referenceLevelName(level);
  if (name === undefined) return undefined;
  const [energy, geometry] = name.split("//");
  return geometry === undefined ? { energy } : { energy, geometry };
}

/**
 * The reference row's tooltip detail: the DFT method/basis the record declares
 * (`qm_method`, `"B3LYP/6-31G*"`), or `undefined`. Kept out of the legend —
 * the legend names the level, and the method is detail about it (#2026).
 */
export function qmSeriesDetail(profile: DihedralProfile): string | undefined {
  const declared = profile.qm_method;
  return typeof declared === "string" && declared.length > 0 ? declared : undefined;
}

/**
 * Why the fitted curve is missing, when it is.
 *
 * `null` means it is not missing. `"decided"` means a positive, recorded
 * scientific decision (`fit_status` says GAFF2 was kept), which the caller
 * captions as such. `"gap"` means we do not know: the artifact predates #869,
 * the driver returned nothing, the block was dropped, or the overlay never
 * joined. `"misaligned"` means a curve arrived on a different grid.
 *
 * The `"gap"` case is the one Rupert blocked #878 over. It renders as a
 * neutral "not published" note, never as "GAFF2 retained" — a torsion whose
 * refitted parameters ARE in the force field must not be captioned as having
 * kept standard GAFF2 just because its curve did not reach the client.
 */
export type FitAbsence = "decided" | "gap" | "misaligned" | null;

export function fitAbsence(profile: DihedralProfile): FitAbsence {
  const fitted = numbers(profile.fitted_relative_kcal_per_mol);
  if (fitted.length > 0) {
    return fitted.length === numbers(profile.angles_deg).length ? null : "misaligned";
  }
  return isGaffRetained(profile) ? "decided" : "gap";
}
